import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Fab,
  Button,
} from '@mui/material';
import {
  Star,
  Grade,
  History,
  AdminPanelSettings,
  ArrowBack,
  Refresh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SiteWrapper from '../../home-pages/SiteWrapper';
import StoreItemCard from './StoreItemCard';
import { getStoreConfigAsync, migrateToFirestore, clearStoreConfigCache } from '../../../utils/storeConfig';
import { useFirebaseAuthentication } from 'lib/firebase';
import { createPurchase, getUserPoints, isAdmin } from 'lib/purchaseUtils';
import { getUserLevelInfo } from '../../../utils/xpUtils';

const ChoreStore = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const user = useFirebaseAuthentication();
  
  // User state
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null); // Track which item is being purchased
  const [storeItems, setStoreItems] = useState([]); // Store items from Firestore
  const [storeLoading, setStoreLoading] = useState(true); // Loading state for store items
  const [levelInfo, setLevelInfo] = useState({
    level: 1,
    currentXP: 0,
    nextLevelXP: 50,
    progressPercentage: 0
  });
  
  const userIsAdmin = user && isAdmin(user);

  // Load user points function (reusable)
  const loadUserPoints = useCallback(async () => {
    if (user) {
      try {
        const points = await getUserPoints(user.uid);
        setUserPoints(points);
        
        // Calculate level info based on points
        const levelData = getUserLevelInfo(points);
        setLevelInfo(levelData);
      } catch (error) {
        enqueueSnackbar('Error loading your points', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  }, [user, enqueueSnackbar]);

  // Load user points on component mount
  useEffect(() => {
    loadUserPoints();
  }, [loadUserPoints]);

  // Load store items from Firestore
  useEffect(() => {
    const loadStoreItems = async () => {
      try {
        setStoreLoading(true);
        
        // Attempt migration on first load if needed
        const items = await getStoreConfigAsync();
        setStoreItems(items);
        
        // Optional: Attempt migration from localStorage to Firestore
        // This will only run once and migrate existing data
        try {
          await migrateToFirestore();
        } catch (migrationError) {
        }
        
      } catch (error) {
        enqueueSnackbar('Failed to load store items', { variant: 'error' });
      } finally {
        setStoreLoading(false);
      }
    };

    loadStoreItems();
  }, [enqueueSnackbar]);

  // Get store items from configuration
  const handlePurchase = async (item) => {
    if (!user) {
      enqueueSnackbar('Please sign in to make purchases', { variant: 'error' });
      return;
    }

    setPurchasing(item.id);
    
    try {
      await createPurchase(user.uid, user.displayName || user.email, item);
      
      // Reload user points to reflect immediate deduction
      await loadUserPoints();
      
      enqueueSnackbar(`Purchase made! ${item.price} points deducted. Waiting for parent approval. 🎉`, { 
        variant: 'success',
        autoHideDuration: 5000,
      });
      
    } catch (error) {
      
      // Check if it's an insufficient points error from the new validation
      if (error.message && error.message.includes('Insufficient points')) {
        enqueueSnackbar(error.message, { 
          variant: 'error',
          autoHideDuration: 4000,
        });
      } else {
        enqueueSnackbar('Error creating purchase. Please try again.', { 
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
      
      // Reload points in case of any inconsistency
      await loadUserPoints();
    } finally {
      setPurchasing(null);
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return '#28a745';
      case 'rare': return '#007bff';
      case 'epic': return '#6f42c1';
      case 'legendary': return '#fd7e14';
      default: return '#6c757d';
    }
  };

  const handleRefreshStore = async () => {
    clearStoreConfigCache();
    setStoreLoading(true);
    try {
      const freshItems = await getStoreConfigAsync(true); // Force refresh
      setStoreItems(freshItems || []);
      enqueueSnackbar('Store refreshed!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error refreshing store', { variant: 'error' });
    } finally {
      setStoreLoading(false);
    }
  };

  return (
    <SiteWrapper>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 3,
                mb: 4,
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                }}
              />
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  {/* Back Button */}
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={() => navigate('/chores')}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      Back to Chores
                    </Button>
                  </Box>
                  
                  <Typography
                    variant={isMobile ? 'h4' : 'h3'}
                    fontWeight="bold"
                    gutterBottom
                    sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
                  >
                    🏪 Chore Store
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Spend your hard-earned points on awesome rewards!
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isMobile ? 'flex-start' : 'flex-end',
                      gap: 2,
                    }}
                  >
                    {/* Points Display */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        background: 'rgba(255,255,255,0.2)',
                        px: 2,
                        py: 1,
                        borderRadius: 20,
                      }}
                    >
                      <Star sx={{ color: '#ffd700' }} />
                      <Typography variant="h6" fontWeight="bold">
                        {userPoints} Points
                      </Typography>
                    </Box>

                    {/* Level Display */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        background: 'rgba(255,255,255,0.2)',
                        px: 2,
                        py: 1,
                        borderRadius: 20,
                      }}
                    >
                      <Grade sx={{ color: '#ffd700' }} />
                      <Typography variant="body1" fontWeight="bold">
                        Level {levelInfo.level}
                      </Typography>
                    </Box>

                    {/* XP Progress */}
                    <Box sx={{ width: '200px', maxWidth: '100%' }}>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        XP: {levelInfo.currentXP}/{levelInfo.nextLevelXP}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={levelInfo.progressPercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(255,255,255,0.3)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#ffd700',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>

          {/* Featured Items Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              color="white"
              gutterBottom
              sx={{
                textAlign: 'center',
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              }}
            >
              ⭐ Featured Items
            </Typography>

            <Grid container spacing={3}>
              {storeLoading ? (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      py: 4 
                    }}
                  >
                    <Typography variant="h6" color="white" sx={{ textAlign: 'center' }}>
                      Loading store items...
                    </Typography>
                  </Box>
                </Grid>
              ) : storeItems.length === 0 ? (
                <Grid item xs={12}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      py: 4 
                    }}
                  >
                    <Typography variant="h6" color="white" sx={{ textAlign: 'center' }}>
                      No store items available at the moment.
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                storeItems.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      <StoreItemCard
                        item={item}
                        onPurchase={handlePurchase}
                        userPoints={userPoints}
                        getRarityColor={getRarityColor}
                        isPurchasing={purchasing === item.id}
                        disabled={loading || storeLoading}
                      />
                    </motion.div>
                  </Grid>
                ))
              )}
            </Grid>
          </motion.div>

          {/* Coming Soon Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Paper
              elevation={4}
              sx={{
                mt: 6,
                p: 4,
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: 'white',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                🚀 More Items Coming Soon!
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Keep earning points to unlock amazing new rewards. 
                The store will expand with more treats, privileges, and special items!
              </Typography>
            </Paper>
          </motion.div>
        </Container>

        {/* Floating Action Buttons */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Purchase History Button */}
          <Fab
            color="primary"
            onClick={() => navigate('/purchases')}
            sx={{
              bgcolor: '#4facfe',
              '&:hover': { bgcolor: '#0084e3' },
            }}
          >
            <History />
          </Fab>

          {/* Refresh Store Button */}
          <Fab
            color="secondary"
            onClick={handleRefreshStore}
            disabled={storeLoading}
            sx={{
              bgcolor: '#28a745',
              '&:hover': { bgcolor: '#218838' },
              '&:disabled': { bgcolor: '#6c757d' },
            }}
          >
            <Refresh />
          </Fab>

          {/* Admin Panel Button (only for admins) */}
          {userIsAdmin && (
            <Fab
              color="secondary"
              onClick={() => navigate('/admin')}
              sx={{
                bgcolor: '#e74c3c',
                '&:hover': { bgcolor: '#c0392b' },
              }}
            >
              <AdminPanelSettings />
            </Fab>
          )}
        </Box>
      </Box>
    </SiteWrapper>
  );
};

export default ChoreStore;